import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Button } from '@abyss/web/ui/Button';
import { Drawer } from '@abyss/web/ui/Drawer';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Popover } from '@abyss/web/ui/Popover';
import { Text } from '@abyss/web/ui/Text';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CompareProvider, Provider } from '../../models/Provider';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { getDistance } from '../../utils/card.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../AdobeTagging/adobeModalTrackEvent';
import { adobeSearchResultClickEvent } from '../AdobeTagging/adobeSearchResultClickEvent';
import { Constants } from '../Constants';
import { phoneOnly, tabletScreen } from '../ConstantsStyles';
import {
  convertProviderTypeToAdobeType,
  formatProviderId,
  handleAdobeSearchResultClickAndModalEvent,
} from '../Utils/adobeTrackUtils/adobeTrackUtils';
import {
  Dot,
  milesButtonStyles,
  milesButtonStylesListView,
} from './DataCard.style';
import {
  IconMaterialMobile,
  IconMaterialWeb,
  MilesContainer,
  MobileMilesContainer,
  popoverMobileStyle,
} from './Miles.style';

type Props = {
  color?: string;
  distance: string;
  enableMapViewHideOption?: boolean;
  fontWeight?: string;
  headers?: ResponseHeaders;
  id?: string;
  popupContainer?: boolean;
  indexForAnalytics?: number;
  sectionType?: string;
  practitioner?: Provider;
  providerType?: string;
  locationForAnalytics?: string;
  selectedFilters?: string;
  showBestMatchDetails?: boolean;
  selectedProvider?: CompareProvider | any;
  calledFrom?: string;
  searchTerm?: string;
  onClosePopup?: () => void;
  isCTADisplayLogic?: boolean;
  onMilesClick?: () => void;
  showSmartChoice?: boolean;
  isNewMapViewDataCardDesign?: boolean;
  isGroupedLocationDrawer?: boolean;
  addressMilesRef?: any;
  expandedProviderListView?: boolean;
};

const showDotAfterAddress = (
  isCTADisplayLogic,
  popupContainer,
  isGroupedLocationDrawer,
  enableMapViewHideOption,
  expandedProviderListView,
  mobileScreen = false
) =>
  isCTADisplayLogic &&
  !popupContainer &&
  !isGroupedLocationDrawer && (
    <Dot
      style={
        enableMapViewHideOption
          ? {
              position: 'relative',
              left: !expandedProviderListView && mobileScreen ? '-2px' : '0px',
              ...(!expandedProviderListView && { alignSelf: 'flex-start' }),
              top: expandedProviderListView
                ? '-10px'
                : mobileScreen
                ? '0px'
                : '12px',
            }
          : {}
      }
    />
  );

export const Miles = ({
  color = '$interactive1',
  distance,
  enableMapViewHideOption,
  isNewMapViewDataCardDesign,
  fontWeight = '$bold',
  headers = { correlationId: '' },
  id,
  sectionType,
  indexForAnalytics,
  popupContainer,
  providerType,
  locationForAnalytics,
  practitioner = {},
  selectedFilters,
  showBestMatchDetails = false,
  calledFrom,
  searchTerm,
  selectedProvider = {},
  onClosePopup,
  isCTADisplayLogic = false,
  onMilesClick,
  showSmartChoice,
  isGroupedLocationDrawer = false,
  addressMilesRef = null,
  expandedProviderListView = false,
}: Props) => {
  const { t } = useTranslation();
  const distanceTextWithMiles: string = `${getDistance(distance)} ${t(
    'miles'
  )}`;
  const mobileScreen = useMediaQuery(phoneOnly);
  const TabletScreen = useMediaQuery(tabletScreen);
  const [isOpen, setIsOpen] = useState(false);
  const [hasWrapped, setHasWrapped] = useState(false);
  const distanceDirectionTestId: string = 'distance-direction';
  const resultCardLocationInfoPopOverDataTestId: string = mobileScreen
    ? 'provider-location-detail-miles-Info-drawer'
    : 'provider-location-detail-miles-Info-popover';
  const modalEventName: string = Constants.CONTENT_FILTERS.DISTANCE;

  const locationForAdobe = `modal:compare card:position ${indexForAnalytics}`;

  const linkTrack = () => {
    adobeLinkTrackEvent({
      name: modalEventName,
      location: locationForAdobe,
      type: 'internal',
      customAttributesBlock: {
        correlationId: headers.correlationId,
        providerId: formatProviderId(selectedProvider),
        providerType: convertProviderTypeToAdobeType(
          selectedProvider?.providerType
        ),
      },
    });
  };
  const searchTrack = () => {
    adobeSearchResultClickEvent({
      filters: selectedFilters,
      linkName: 'distance',
      linkPosition: indexForAnalytics,
      linkLocation: locationForAnalytics,
      customAttributesBlock: {
        correlationId: headers.correlationId,
        providerId: formatProviderId(practitioner),
        providerType: convertProviderTypeToAdobeType(providerType),
      },
      term: searchTerm ?? '',
    });
  };
  const trackAdobeEvent = () => {
    if (calledFrom && calledFrom === 'comparecard') {
      linkTrack();
    } else {
      searchTrack();
    }
  };
  useEffect(() => {
    if (isOpen) {
      if (calledFrom && calledFrom === 'comparecard') {
        linkTrack();
        adobeModalTrackEvent(modalEventName);
      } else {
        handleAdobeSearchResultClickAndModalEvent({
          filters: selectedFilters,
          linkName: resultCardLocationInfoPopOverDataTestId,
          linkPosition: indexForAnalytics,
          linkLocation: locationForAnalytics,
          modalName: modalEventName,
          customAttributesBlock: {
            correlationId: headers.correlationId,
            providerId: formatProviderId(practitioner),
            providerType: convertProviderTypeToAdobeType(providerType),
          },
          term: searchTerm ?? '',
        });
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const checkIfWrapped = () => {
      if (addressMilesRef) {
        const container = addressMilesRef.current;
        if (container) {
          const children = Array.from(
            (Array.from(container.children)?.[0] as HTMLElement)?.children
          );
          if (children.length > 1) {
            const firstChildTop = (children[0] as HTMLElement).offsetTop;
            const childWrapped = children.some(
              (child) => (child as HTMLElement).offsetTop !== firstChildTop
            );
            setHasWrapped(childWrapped);
          }
        }
      }
    };

    let timeoutId: NodeJS.Timeout | null = null;

    const debounceCheckIfWrapped = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        checkIfWrapped();
      }, 200);
    };

    debounceCheckIfWrapped();

    window.addEventListener('resize', debounceCheckIfWrapped);
    return () => {
      window.removeEventListener('resize', debounceCheckIfWrapped);
    };
  }, []);

  const MilesWithInfoIcon = () => (
    <Button
      className="icon-text-container"
      css={
        enableMapViewHideOption ? milesButtonStylesListView : milesButtonStyles
      }
      data-auto-testid={distanceDirectionTestId}
      data-testid={distanceDirectionTestId}
      onClick={onMilesClick}
      style={{
        position: 'relative',
        top: expandedProviderListView ? '-9px' : '0',
      }}
      variant="ghost"
    >
      <Text
        color={isCTADisplayLogic || !showBestMatchDetails ? '$gray7' : color}
        css={{
          fontSize:
            popupContainer || isNewMapViewDataCardDesign
              ? '$sm'
              : expandedProviderListView
              ? TabletScreen
                ? '$sm'
                : 'md'
              : '',
          fontWeight: popupContainer
            ? '700'
            : enableMapViewHideOption
            ? '600'
            : '400',
          color: popupContainer ? '$interactive1' : '$gray7',
          '@screen < $md': {
            ...(enableMapViewHideOption && { fontSize: '$extraSmallText' }),
          },
          '@screen < $sm': {
            ...(!enableMapViewHideOption && { fontSize: '$sm' }),
            lineHeight: '18px',
          },
          cursor: 'pointer',
        }}
        data-auto-testid={`data-card-distance-miles-count-${sectionType}-${id}`}
        data-testid={`data-card-distance-miles-count-${sectionType}-${id}`}
        fontWeight={
          isCTADisplayLogic || !showBestMatchDetails ? '$normal' : fontWeight
        }
      >
        {distanceTextWithMiles}
      </Text>
    </Button>
  );

  const MilesGroupedLocationDrawer = () => (
    <Flex css={{ paddingTop: '5px' }}>
      {!hasWrapped && (
        <Dot
          style={
            enableMapViewHideOption
              ? {
                  left: '-3px',
                  position: 'relative',
                  top: '7px',
                }
              : {}
          }
        />
      )}
      <Text
        color="$gray7"
        css={{
          fontWeight: '600',
          fontSize: '14px',
          lineHeight: '18px',
          paddingLeft: hasWrapped ? '0px' : '5px',
          paddingRight: hasWrapped ? '10px' : '0px',
          '@media (max-width: 1248px)': {
            fontSize: '12px',
          },
        }}
        data-auto-testid={`data-card-distance-miles-count-${sectionType}-${id}`}
        data-testid={`data-card-distance-miles-count-${sectionType}-${id}`}
      >
        {distanceTextWithMiles}
      </Text>
    </Flex>
  );

  const wrapInCaseOfNonSmartChoice = showSmartChoice
    ? { flexWrap: 'nowrap', maxWidth: '220px' }
    : { flexWrap: 'nowrap' };

  if (isGroupedLocationDrawer) {
    return <MilesGroupedLocationDrawer />;
  }

  return (
    <React.Fragment>
      {!mobileScreen ? (
        <MilesContainer
          css={wrapInCaseOfNonSmartChoice}
          cssProps={{ enableMapViewHideOption }}
        >
          {showDotAfterAddress(
            isCTADisplayLogic,
            popupContainer,
            isGroupedLocationDrawer,
            enableMapViewHideOption,
            expandedProviderListView,
            mobileScreen
          )}
          {isCTADisplayLogic && <MilesWithInfoIcon />}
          {!enableMapViewHideOption ? (
            <Popover
              align="center"
              aria-label={`${distanceTextWithMiles} ${t(
                'ACCESSIBILITY_LABELS.INFORMATION_ABOUT_DISTANCE'
              )}`}
              content={t('DISTANCE_POPOVER_CONTENT')}
              css={{
                '&:focus-visible': {
                  margin: '5px',
                },
              }}
              data-auto-testid={resultCardLocationInfoPopOverDataTestId}
              data-testid={resultCardLocationInfoPopOverDataTestId}
              onClick={(event) => {
                trackAdobeEvent();
                event.stopPropagation();
              }}
              onOpenChange={() => {
                setIsOpen(!isOpen);
              }}
              open={isOpen}
              title={<Text size="$sm">{t('CONTENT_FILTERS.DISTANCE')}</Text>}
              width={220}
            >
              <Layout.Group space={4}>
                {!isCTADisplayLogic && <MilesWithInfoIcon />}
                <IconMaterial
                  css={IconMaterialWeb}
                  data-auto-testid="provider-location-detail-miles-Info"
                  data-testid="provider-location-detail-miles-Info"
                  icon="info"
                  size={popupContainer ? 18 : 20}
                  tabIndex={-1}
                  variant="outlined"
                />
              </Layout.Group>
            </Popover>
          ) : (
            <Layout.Group space={4}>
              {!isCTADisplayLogic && <MilesWithInfoIcon />}
            </Layout.Group>
          )}
        </MilesContainer>
      ) : (
        <Flex alignItems="center" justify="start">
          {showDotAfterAddress(
            isCTADisplayLogic,
            popupContainer,
            isGroupedLocationDrawer,
            enableMapViewHideOption,
            expandedProviderListView,
            mobileScreen
          )}
          <Drawer
            css={popoverMobileStyle}
            data-auto-testid={resultCardLocationInfoPopOverDataTestId}
            data-testid={resultCardLocationInfoPopOverDataTestId}
            isOpen={isOpen}
            onClose={() => {
              setIsOpen(false);
              if (onClosePopup) onClosePopup();
            }}
            position="bottom"
            title={t('CONTENT_FILTERS.DISTANCE')}
          >
            <Drawer.Section>{t('DISTANCE_POPOVER_CONTENT')}</Drawer.Section>
          </Drawer>
          <Button
            className="icon-text-container"
            css={{
              padding: '0px',
              '@screen < $sm': {
                padding: '0px 4px 0px 4px',
              },
              height: 'auto',
            }}
            data-auto-testid={distanceDirectionTestId}
            data-testid={distanceDirectionTestId}
            onClick={onMilesClick}
            style={{
              ...(enableMapViewHideOption && {
                top: '-9px',
                padding: '0px 4px 0px 2px',
              }),
            }}
            variant="ghost"
            {...(!showBestMatchDetails && { tabIndex: -1 })}
          >
            <Text
              color={
                isCTADisplayLogic || !showBestMatchDetails ? '$gray7' : color
              }
              css={{
                fontSize:
                  popupContainer || isNewMapViewDataCardDesign ? '$sm' : '$md',
                '@screen < $sm': {
                  fontSize: enableMapViewHideOption ? '$extraSmallText' : '$sm',
                  fontWeight: enableMapViewHideOption ? '$semibold' : '$normal',
                },
              }}
              data-auto-testid={`data-card-distance-miles-count-${sectionType}-${id}`}
              data-testid={`data-card-distance-miles-count-${sectionType}-${id}`}
              fontWeight={
                isCTADisplayLogic || !showBestMatchDetails
                  ? '$normal'
                  : fontWeight
              }
            >
              {distanceTextWithMiles}
            </Text>
          </Button>
          {!enableMapViewHideOption && (
            <MobileMilesContainer
              aria-haspopup="dialog"
              data-auto-testid="provider-location-detail-miles-Info"
              data-testid="provider-location-detail-miles-Info"
              onClick={(event) => {
                trackAdobeEvent();
                setIsOpen(true);
                event?.stopPropagation();
              }}
            >
              <IconMaterial
                css={IconMaterialMobile}
                icon="info"
                size={18}
                variant="outlined"
              />
            </MobileMilesContainer>
          )}
        </Flex>
      )}
    </React.Fragment>
  );
};
